import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-rose/15 text-rose",
  ordered: "bg-beige text-espresso",
};

const LABEL = {
  pending: "Pending Approval",
  approved: "Approved — Ready to Order",
  rejected: "Pre-Order Unavailable",
  ordered: "Ordered",
};

export const AdminPreorders = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/admin/preorders`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setRows(d.preorders || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, status) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/preorders/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw await r.json();
      toast.success(status === "approved" ? "Pre-order approved — customer can now order" : "Pre-order rejected");
      load();
    } catch (err) {
      toast.error(err?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8">
      <p className="text-sm text-taupe mb-6">
        SAFARI pre-order requests. Approve to let the customer place a normal order for the approved shade and quantity — reject to mark it unavailable.
      </p>
      {rows.length === 0 && <p className="text-sm text-espresso/60" data-testid="preorders-empty">No pre-order requests yet.</p>}
      <div className="space-y-3">
        {rows.map((p) => (
          <div key={p.id} className="border border-beige bg-white p-5 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid={`preorder-row-${p.id}`}>
            <div className="min-w-[180px]">
              <p className="text-sm font-medium">{p.product_name} — {p.shade}</p>
              <p className="text-xs text-taupe mt-0.5">{p.collection} · Qty {p.qty}</p>
            </div>
            <div className="min-w-[200px]">
              <p className="text-sm">{p.customer?.name}</p>
              <p className="text-xs text-taupe mt-0.5">{p.customer?.email} · {p.customer?.phone}</p>
            </div>
            <p className="text-xs text-taupe">{new Date(p.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
            <span className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 ${BADGE[p.status] || "bg-beige"}`} data-testid={`preorder-status-${p.id}`}>
              {LABEL[p.status] || p.status}
            </span>
            {p.order_number && <span className="text-xs text-taupe">Order {p.order_number}</span>}
            {p.status === "pending" && (
              <div className="flex gap-2 ml-auto">
                <button disabled={busy} data-testid={`preorder-approve-${p.id}`} onClick={() => decide(p.id, "approved")} className="btn-primary !px-5 !py-2.5">
                  Approve
                </button>
                <button disabled={busy} data-testid={`preorder-reject-${p.id}`} onClick={() => decide(p.id, "rejected")} className="btn-secondary !px-5 !py-2.5 !text-rose !border-rose/50">
                  Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
